import { useMemo } from 'react'
import * as Sentry from '@sentry/browser'
import { QueryCache, QueryClient } from '@tanstack/react-query'
import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import type { ApiError } from '~/adapter/api/error'
import {
  getRequestError,
  isAbortError,
  isAppVersionInconsistentError,
  isAuthError,
  isNetworkError,
} from '~/adapter/api/error'
import { useClearAllPersistedData } from '~/app/hooks/useClearAllPersistedData'
import { routes } from '~/app/routes/paths'
import { appMode } from '~/config'
import { useLogout } from '~/domain/logout/useLogout'
import { useAppVersionConsistency } from '~/stores/appVersionConsistency/appVersionConsistency'
import { useKioskAuthErrorStore } from '~/stores/kioskAuthError/kioskAuthError'
import { ErrorWithExtra } from '~/utils/error'
import { replaceLocation } from '~/utils/navigation'
import type { DefaultOptions } from '@tanstack/react-query'

const handleApiError = async (
  logout: ReturnType<typeof useLogout>['logout'],
  clearAllPersistedData: () => void,
  inconsistentVersionDetected: () => void,
  error: { type: 'apiError' } & ApiError,
) => {
  if (isAuthError(error)) {
    // CredentialsNotFoundError は「未ログイン」状態（正常系）として扱う。
    // /auth/logout は呼ばず（そもそもログインしていない）、永続データをクリアして /login へ遷移する。
    // logout が走らないので、下の "enforced logout" Sentry 通知の対象からも外す。
    if (error.code === ErrorCodeEnum.CredentialsNotFoundError) {
      clearAllPersistedData()
      // キオスク端末（kiosk）では /login へ遷移せず全画面エラー（KioskAuthError）を被せる。
      // 証明書なし端末は fetchSession 失敗後も /me を叩いてここに来るため、/login へ
      // 遷移させると「/ → /me 401 → /login → / …」のフルリロードループになる。
      if (appMode.isKiosk) {
        useKioskAuthErrorStore.getState().kioskAuthErrorDetected('device_setup')
        return
      }
      replaceLocation(routes.login.buildPath({}))
      return
    }

    Sentry.withScope(scope => {
      scope.setLevel('info')
      Sentry.captureException(
        new ErrorWithExtra(
          'AuthError',
          'queryClient enforced logout with error code:' + error.code,
          error,
        ),
      )
    })
    await logout({ userInitiated: false, errorCode: error.code })
    return
  }

  if (isAppVersionInconsistentError(error)) {
    inconsistentVersionDetected()
    return
  }

  Sentry.captureException(
    new ErrorWithExtra(
      'ServerReturnedSomeError',
      'API Server returned an error',
      error,
    ),
  )
}

const queryOptions: DefaultOptions = {
  queries: {
    // <networkMode>
    // @doc: https://tanstack.com/query/v4/docs/react/guides/network-mode#network-mode-always
    // defaultでは`online`になり、オフライン状態ではリクエストを投げずonErrorでエラーをキャッチできないので、
    // 基本的には`always`にしておく
    networkMode: 'always',
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    throwOnError: true,
  },
}

export const useAppQueryClient = () => {
  const { logout } = useLogout()
  const { clearAllPersistedData } = useClearAllPersistedData()

  const inconsistentVersionDetected = useAppVersionConsistency(
    state => state.inconsistentVersionDetected,
  )

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          ...queryOptions,
          mutations: {
            networkMode: 'always',
            // mutationのonErrorは呼び出し側で制御するためにオーバーライド可能な作りにしたいので、
            // mutationCacheではなくdefaultOptionsにonErrorを設定
            onError: async e => {
              // 画面遷移で in-flight mutation が中断された AbortError は想定内なので breadcrumb に留める。
              // ネットワーク断(TypeError)は query と違い mutation では再スローされず、
              // コードバグの TypeError と型で区別できないため、ここでは抑制せず下で捕捉する。
              if (isAbortError(e)) {
                Sentry.addBreadcrumb({
                  category: 'mutation',
                  level: 'info',
                  message: 'mutation aborted',
                })
                return
              }
              const error = await getRequestError(e)
              if (error.type === 'unexpectedError') {
                Sentry.captureException(
                  new ErrorWithExtra(
                    'MutateError',
                    'mutation onError handle UnexpectedError',
                    { e },
                  ),
                )
                return
              }
              await handleApiError(
                logout,
                clearAllPersistedData,
                inconsistentVersionDetected,
                error,
              )
            },
          },
        },
        queryCache: new QueryCache({
          onError: async e => {
            // ネットワーク断・リクエスト中断は想定内の失敗なので breadcrumb に留める。
            if (isNetworkError(e) || isAbortError(e)) {
              Sentry.addBreadcrumb({
                category: 'query-cache',
                level: 'info',
                message: 'query-cache network/abort error',
              })
              return
            }
            const error = await getRequestError(e)
            // queryCacheでhandleするエラーはapiErrorのみのはずなのでそれ以外のエラーは特別なエラーを出す
            // (Response だが schema 不一致の gateway エラーページ等はここで捕捉し、本物のインフラ異常として残す)
            if (error.type === 'unexpectedError') {
              Sentry.captureException(
                new ErrorWithExtra(
                  'QueryCacheError',
                  'QueryCache handle UnexpectedError',
                  { e },
                ),
              )
              return
            }
            await handleApiError(
              logout,
              clearAllPersistedData,
              inconsistentVersionDetected,
              error,
            )
          },
        }),
      }),
    [clearAllPersistedData, inconsistentVersionDetected, logout],
  )

  return { queryClient }
}
